import styled from "@emotion/styled";
import { useLocation } from "react-router-dom";
import {
  Button,
  FlexRow,
  IconNewReview,
  Link,
  Stack,
  Text,
  Tooltip,
} from "atoms";
import { trackEvent } from "contexts/mixpanel";
import { paths } from "utils/paths";

const trackSignUp = () =>
  trackEvent({
    name: "Button Click",
    ButtonName: "Sign Up",
    ButtonContext: "Review Button",
  });
const trackSignIn = () =>
  trackEvent({
    name: "Button Click",
    ButtonName: "Sign In",
    ButtonContext: "Review Button",
  });

type ReviewLinkProps = {
  userIsSignedIn: boolean;
  userIsCurrentArtist: boolean;
  userHasReviewed: boolean;
  artistId: string;
  size?: React.ComponentProps<typeof Button>["size"];
};

function ReviewLink({
  userIsSignedIn,
  userIsCurrentArtist,
  userHasReviewed,
  artistId,
  size = "xs",
  ...props
}: ReviewLinkProps) {
  if (!userIsSignedIn) {
    return (
      <Tooltip
        positionY="bottom"
        positionX="center"
        label={
          <Button variant="primary" size={size} as="div" {...props}>
            Write a review <IconNewReview />
          </Button>
        }
      >
        <ReviewSigninContent />
      </Tooltip>
    );
  }

  // User has not already reviewed and is not on their own profile page
  return !userIsCurrentArtist && !userHasReviewed ? (
    <Link
      variant="primary"
      size={size}
      to={paths.artistReviewCreate(artistId)}
      {...props}
    >
      Review <IconNewReview />
    </Link>
  ) : null;
}

function ReviewSigninContent() {
  // Successful sign-in will redirect, but we want to end up at the review form and not back here
  const { pathname, ...location } = useLocation();
  const from = {
    ...location,
    pathname: pathname.endsWith("/review") ? pathname : `${pathname}/review`,
  };

  return (
    <Stack gap="sm">
      <SText
        bold
        size="xs"
        font="display"
        textAlign="center"
        textTransform="uppercase"
      >
        Create an account <br />
        to&nbsp;write&nbsp;a&nbsp;review
      </SText>
      <FlexRow gap="10px" flexWrap="nowrap">
        <Link
          to="/sign-in"
          state={{ from }}
          variant="primary"
          size="xxs"
          onClick={trackSignIn}
        >
          Sign In
        </Link>
        <Link
          to="/register"
          state={{ from }}
          variant="primary"
          size="xxs"
          onClick={trackSignUp}
        >
          Create Account
        </Link>
      </FlexRow>
    </Stack>
  );
}

const SText = styled(Text)(({ theme }) => ({
  lineHeight: 1.3,
  color: theme.colors.fg,
}));

export { ReviewLink };
